// frontend-react/src/pages/DepartmentManagementPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../config/api';
import './DepartmentManagementPage.css';
import PageLayout from '../components/PageLayout';

interface Department {
  id: number;
  name: string;
  company_id: number;
  manager_id: number | null;
  manager_name: string | null;
  manager_email: string | null;
  is_active: boolean;
  created_at: string;
  member_count: number;
}

interface User {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  position: string | null;
  is_active: boolean;
}

interface DepartmentMember {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  position: string | null;
  is_manager: boolean;
}

type ManageTab = 'manager' | 'members';

const DepartmentManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // 獲取 Auth Headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token'); // 修正：使用正確的 key
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [departmentMembers, setDepartmentMembers] = useState<{ [key: number]: DepartmentMember[] }>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageTab, setManageTab] = useState<ManageTab>('manager');
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null);
  const [selectedAddUserId, setSelectedAddUserId] = useState<number | null>(null);
  const [selectedRemoveUserId, setSelectedRemoveUserId] = useState<number | null>(null);
  const [pendingAdd, setPendingAdd] = useState<User[]>([]);
  const [pendingRemove, setPendingRemove] = useState<DepartmentMember[]>([]);

  // 判斷是否為 ADMIN（含 SUPER_ADMIN）
  const isAdmin =
    user?.is_superadmin || user?.roles?.includes('ADMIN') || user?.roles?.includes('SUPER_ADMIN');
  // 判斷是否為部門主管
  const isDepartmentManager = user?.is_department_manager;

  // 檢查權限（ADMIN 或 部門主管可訪問）
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // 檢查是否為 ADMIN 或 部門主管
    if (!isAdmin && !isDepartmentManager) {
      alert('您需要是 ADMIN 或部門主管才能訪問此頁面');
      navigate('/');
      return;
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, isAdmin, isDepartmentManager]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 載入部門列表
      const deptResponse = await fetch(`${getApiBaseUrl()}/api/v1/departments/list`, {
        headers: getAuthHeaders(),
      });

      if (deptResponse.ok) {
        const deptData = await deptResponse.json();
        setDepartments(deptData);

        // 載入每個部門的成員資訊
        const membersData: { [key: number]: DepartmentMember[] } = {};
        for (const dept of deptData) {
          try {
            const membersResponse = await fetch(
              `${getApiBaseUrl()}/api/v1/departments/${dept.id}/members`,
              { headers: getAuthHeaders() }
            );
            if (membersResponse.ok) {
              membersData[dept.id] = await membersResponse.json();
            }
          } catch (error) {
            console.error(`載入部門 ${dept.id} 成員失敗:`, error);
            membersData[dept.id] = [];
          }
        }
        setDepartmentMembers(membersData);
      }

      // 載入使用者列表（用於指派主管）
      const userResponse = await fetch(`${getApiBaseUrl()}/api/v1/user/company-users`, {
        headers: getAuthHeaders(),
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUsers(userData.users || []);
      }
    } catch (error) {
      console.error('載入資料失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/departments/available-users`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data);
      }
    } catch (error) {
      console.error('載入可用使用者失敗:', error);
    }
  };

  const handleCreateDepartment = async () => {
    if (!newDepartmentName.trim()) {
      alert('請輸入部門名稱');
      return;
    }

    try {
      const headers = getAuthHeaders();

      const response = await fetch(`${getApiBaseUrl()}/api/v1/departments/create`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newDepartmentName }),
      });

      if (response.status === 401) {
        alert('認證失敗，請重新登入');
        return;
      }

      if (response.ok) {
        alert('部門建立成功！');
        setShowCreateModal(false);
        setNewDepartmentName('');
        loadData();
      } else {
        const error = await response.json();
        console.error('Error response:', error);
        alert(`建立失敗：${error.detail || '未知錯誤'}`);
      }
    } catch (error) {
      console.error('建立部門失敗:', error);
      alert('建立部門失敗，請稍後再試');
    }
  };

  const handleAssignManager = async () => {
    if (!selectedDepartment) return;

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/departments/${selectedDepartment.id}/assign-manager`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ manager_id: selectedManagerId }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        setShowManageModal(false);
        setSelectedDepartment(null);
        setSelectedManagerId(null);
        loadData();
      } else {
        const error = await response.json();
        alert(`指派失敗：${error.detail || '未知錯誤'}`);
      }
    } catch (error) {
      console.error('指派主管失敗:', error);
      alert('指派主管失敗，請稍後再試');
    }
  };

  const handleConfirmMemberChanges = async () => {
    if (!selectedDepartment) return;
    if (pendingAdd.length === 0 && pendingRemove.length === 0) return;

    const deptId = selectedDepartment.id;
    const errors: string[] = [];

    await Promise.all([
      ...pendingRemove.map(async member => {
        try {
          const res = await fetch(
            `${getApiBaseUrl()}/api/v1/departments/${deptId}/remove-member?user_id=${member.id}`,
            { method: 'POST', headers: getAuthHeaders() }
          );
          if (!res.ok) {
            const err = await res.json();
            errors.push(`移除「${member.name}」失敗：${err.detail || '未知錯誤'}`);
          }
        } catch {
          errors.push(`移除「${member.name}」時發生錯誤`);
        }
      }),
      ...pendingAdd.map(async u => {
        try {
          const res = await fetch(
            `${getApiBaseUrl()}/api/v1/departments/${deptId}/add-member?user_id=${u.id}`,
            { method: 'POST', headers: getAuthHeaders() }
          );
          if (!res.ok) {
            const err = await res.json();
            errors.push(`新增「${u.name}」失敗：${err.detail || '未知錯誤'}`);
          }
        } catch {
          errors.push(`新增「${u.name}」時發生錯誤`);
        }
      }),
    ]);

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    // 重新載入資料
    const [membersRes, availableRes] = await Promise.all([
      fetch(`${getApiBaseUrl()}/api/v1/departments/${deptId}/members`, {
        headers: getAuthHeaders(),
      }),
      fetch(`${getApiBaseUrl()}/api/v1/departments/available-users`, {
        headers: getAuthHeaders(),
      }),
    ]);
    if (membersRes.ok) {
      const members = await membersRes.json();
      setDepartmentMembers(prev => ({ ...prev, [deptId]: members }));
      setDepartments(prev =>
        prev.map(d => (d.id === deptId ? { ...d, member_count: members.length } : d))
      );
      setSelectedDepartment(prev => (prev ? { ...prev, member_count: members.length } : prev));
    }
    if (availableRes.ok) {
      setAvailableUsers(await availableRes.json());
    }

    closeManageModal();
  };

  const handleDeleteDepartment = async (dept: Department) => {
    if (dept.member_count > 0) {
      alert(`部門「${dept.name}」還有 ${dept.member_count} 位成員，無法刪除`);
      return;
    }

    if (!window.confirm(`確定要刪除部門「${dept.name}」嗎？`)) {
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/departments/${dept.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        alert('部門已刪除');
        loadData();
      } else {
        const error = await response.json();
        alert(`刪除失敗：${error.detail || '未知錯誤'}`);
      }
    } catch (error) {
      console.error('刪除部門失敗:', error);
      alert('刪除部門失敗，請稍後再試');
    }
  };

  const openManageModal = async (dept: Department) => {
    setSelectedDepartment(dept);
    setSelectedManagerId(dept.manager_id);
    setManageTab('manager');
    setShowManageModal(true);
    await loadAvailableUsers();
  };

  const closeManageModal = () => {
    setShowManageModal(false);
    setSelectedDepartment(null);
    setSelectedManagerId(null);
    setSelectedAddUserId(null);
    setSelectedRemoveUserId(null);
    setPendingAdd([]);
    setPendingRemove([]);
    setAvailableUsers([]);
  };

  // SonarQube Fix: Add key handlers for modal overlay accessibility
  const handleModalKeyDown = (e: React.KeyboardEvent, closeModal: () => void) => {
    if (e.key === 'Escape') {
      closeModal();
    }
  };

  const positionMap: Record<string, string> = {
    director: '總監',
    manager: '經理',
    legal: '法務',
    supervisor: '主管',
    senior_specialist: '資深專員',
    specialist: '專員',
    assistant: '助理',
  };

  if (loading) {
    return (
      <PageLayout>
        <div className='department-management-page'>
          <div className='loading-container'>
            <div className='spinner'></div>
            <p>載入中...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // 過濾部門列表：部門主管只能看到自己管理的部門
  const filteredDepartments =
    isDepartmentManager && !isAdmin
      ? departments.filter(dept => dept.manager_id === Number(user?.id))
      : departments;

  return (
    <PageLayout>
      {/* 這一頁自己的內容區塊 */}
      <div className='department-management-page'>
        {/* Page 內部的 Header（標題＋新增按鈕） */}
        <div className='dept-header'>
          <div className='header-left'>
            <h1 className='page-title'>
              <span className='icon'>🏢</span>
              {isAdmin ? '部門管理' : '我的部門'}
            </h1>
          </div>
          {isAdmin && (
            <button className='create-dept-button' onClick={() => setShowCreateModal(true)}>
              <span className='plus-icon'>+</span>
              新增部門
            </button>
          )}
        </div>

        {/* Department Cards Grid */}
        <div className='dept-grid'>
          {filteredDepartments.map(dept => (
            <div key={dept.id} className='dept-card'>
              <div className='dept-card-header'>
                <h3 className='dept-name'>{dept.name}</h3>
                {isAdmin && (
                  <button
                    className='delete-button'
                    onClick={() => handleDeleteDepartment(dept)}
                    title='刪除部門'
                  >
                    ×
                  </button>
                )}
              </div>

              <div className='dept-info'>
                <div className='info-item'>
                  <span className='info-icon'>👤</span>
                  <div className='info-content'>
                    <span className='info-label'>主管</span>
                    <span className='info-value'>
                      {dept.manager_name || <em style={{ color: '#999' }}>未指派</em>}
                    </span>
                  </div>
                </div>

                <div className='info-item'>
                  <span className='info-icon'>👥</span>
                  <div className='info-content'>
                    <span className='info-label'>成員</span>
                    <span className='info-value'>{dept.member_count} 人</span>
                  </div>
                </div>
              </div>

              {/* 成員列表 */}
              {(() => {
                const members = departmentMembers[dept.id];
                return members && members.length > 0 ? (
                  <div className='members-list'>
                    <div className='members-header'>成員名單</div>
                    {members.map(member => {
                      const positionLabel = member.position
                        ? positionMap[member.position] || member.position
                        : '';
                      return (
                        <div key={member.id} className='member-item'>
                          <div className='member-details'>
                            <div className='member-name'>
                              {member.name}
                              {positionLabel && (
                                <span className='member-position-tag'>{positionLabel}</span>
                              )}
                            </div>
                            <div className='member-email'>{member.email}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null;
              })()}

              {(isAdmin || isDepartmentManager) && (
                <button className='assign-button' onClick={() => openManageModal(dept)}>
                  編輯
                </button>
              )}
            </div>
          ))}
        </div>

        {filteredDepartments.length === 0 && (
          <div className='empty-state'>
            <span className='empty-icon'>📂</span>
            <p>{isAdmin ? '目前沒有部門' : '您尚未被指派為任何部門的主管'}</p>
            {isAdmin && (
              <button className='create-first-button' onClick={() => setShowCreateModal(true)}>
                建立第一個部門
              </button>
            )}
          </div>
        )}

        {/* Create Department Modal */}
        {showCreateModal && (
          // SonarQube Fix: Add role, tabIndex, onKeyDown for accessibility
          <div
            className='modal-overlay'
            role='button'
            tabIndex={0}
            onClick={() => setShowCreateModal(false)}
            onKeyDown={e => handleModalKeyDown(e, () => setShowCreateModal(false))}
          >
            {/* SonarQube Fix: Add role="dialog" and aria-modal */}
            <div
              className='modal-content'
              role='dialog'
              aria-modal='true'
              aria-labelledby='create-dept-title'
              onClick={e => e.stopPropagation()}
            >
              <div className='modal-header'>
                <h2 id='create-dept-title'>新增部門</h2>
                <button className='close-button' onClick={() => setShowCreateModal(false)}>
                  ×
                </button>
              </div>
              <div className='modal-body'>
                {/* SonarQube Fix: Label must be associated with a control */}
                <label className='input-label' htmlFor='dept-name-input'>
                  部門名稱
                </label>
                <input
                  id='dept-name-input'
                  type='text'
                  className='text-input'
                  placeholder='請輸入部門名稱（例如：業務一部）'
                  value={newDepartmentName}
                  onChange={e => setNewDepartmentName(e.target.value)}
                  // SonarQube Fix: Use onKeyDown instead of deprecated onKeyPress
                  onKeyDown={e => e.key === 'Enter' && handleCreateDepartment()}
                  autoFocus
                />
              </div>
              <div className='modal-footer'>
                <button className='cancel-button' onClick={() => setShowCreateModal(false)}>
                  取消
                </button>
                <button className='confirm-button' onClick={handleCreateDepartment}>
                  建立
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Department Modal */}
        {showManageModal && selectedDepartment && (
          <div
            className='modal-overlay'
            role='button'
            tabIndex={0}
            onClick={closeManageModal}
            onKeyDown={e => handleModalKeyDown(e, closeManageModal)}
          >
            <div
              className='modal-content modal-content-large'
              role='dialog'
              aria-modal='true'
              aria-labelledby='manage-dept-title'
              onClick={e => e.stopPropagation()}
            >
              <div className='modal-header'>
                <h2 id='manage-dept-title'>管理部門 — {selectedDepartment.name}</h2>
                <button className='close-button' onClick={closeManageModal}>
                  ×
                </button>
              </div>

              {/* Tab Bar */}
              <div className='manage-tabs'>
                <button
                  className={`manage-tab ${manageTab === 'manager' ? 'active' : ''}`}
                  onClick={() => setManageTab('manager')}
                >
                  主管設定
                </button>
                <button
                  className={`manage-tab ${manageTab === 'members' ? 'active' : ''}`}
                  onClick={() => setManageTab('members')}
                >
                  成員管理
                </button>
              </div>

              {/* Tab: 主管設定 */}
              {manageTab === 'manager' && (
                <div className='modal-tab-content'>
                  <div className='modal-body modal-body-grow'>
                    <label className='input-label' htmlFor='manager-select'>
                      選擇主管
                    </label>
                    <select
                      id='manager-select'
                      className='select-input'
                      value={selectedManagerId || ''}
                      onChange={e =>
                        setSelectedManagerId(e.target.value ? Number(e.target.value) : null)
                      }
                    >
                      <option value=''>-- 移除主管 --</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.email}){u.position && ` - ${u.position}`}
                        </option>
                      ))}
                    </select>
                    {selectedDepartment.manager_name && (
                      <p className='current-manager'>
                        目前主管：{selectedDepartment.manager_name} (
                        {selectedDepartment.manager_email})
                      </p>
                    )}
                  </div>
                  <div className='modal-footer'>
                    <button className='confirm-button' onClick={handleAssignManager}>
                      確定
                    </button>
                  </div>
                </div>
              )}

              {/* Tab: 成員管理 */}
              {manageTab === 'members' && (
                <div className='modal-tab-content'>
                  <div className='modal-body modal-members-body'>
                    {/* 移除成員 */}
                    <div className='members-section'>
                      <div className='members-section-title'>移除成員</div>
                      {(() => {
                        const members = (departmentMembers[selectedDepartment.id] || []).filter(
                          m => !pendingRemove.some(pr => pr.id === m.id)
                        );
                        const selectedMember =
                          members.find(m => m.id === selectedRemoveUserId) || null;
                        return (
                          <>
                            {members.length > 0 ? (
                              <div className='add-member-row'>
                                <select
                                  className='select-input'
                                  value={selectedRemoveUserId || ''}
                                  onChange={e =>
                                    setSelectedRemoveUserId(
                                      e.target.value ? Number(e.target.value) : null
                                    )
                                  }
                                >
                                  <option value=''>-- 選擇要移除的成員 --</option>
                                  {members.map(m => {
                                    const positionLabel = m.position
                                      ? positionMap[m.position] || m.position
                                      : '';
                                    return (
                                      <option key={m.id} value={m.id}>
                                        {m.name}
                                        {positionLabel ? ` (${positionLabel})` : ''} — {m.email}
                                      </option>
                                    );
                                  })}
                                </select>
                                <button
                                  className={`member-action-btn remove-btn ${!selectedRemoveUserId || selectedMember?.is_manager ? 'disabled' : ''}`}
                                  disabled={!selectedRemoveUserId || !!selectedMember?.is_manager}
                                  title={
                                    selectedMember?.is_manager
                                      ? '主管無法直接移除，請先變更主管'
                                      : '加入待移除清單'
                                  }
                                  onClick={() => {
                                    if (!selectedRemoveUserId || !selectedMember) return;
                                    setPendingRemove(prev => [...prev, selectedMember]);
                                    setSelectedRemoveUserId(null);
                                  }}
                                >
                                  移除
                                </button>
                              </div>
                            ) : (
                              <p className='no-members-hint'>此部門目前沒有（其他）成員可移除</p>
                            )}
                            {pendingRemove.length > 0 && (
                              <div className='pending-list'>
                                <div className='pending-list-label'>待移除：</div>
                                {pendingRemove.map(m => (
                                  <div key={m.id} className='pending-tag pending-tag-remove'>
                                    {m.name}
                                    <button
                                      className='pending-cancel'
                                      onClick={() =>
                                        setPendingRemove(prev => prev.filter(x => x.id !== m.id))
                                      }
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* 新增成員 */}
                    <div className='members-section'>
                      <div className='members-section-title'>新增成員</div>
                      {(() => {
                        const available = availableUsers.filter(
                          u => !pendingAdd.some(pa => pa.id === u.id)
                        );
                        return (
                          <>
                            {available.length > 0 ? (
                              <div className='add-member-row'>
                                <select
                                  className='select-input'
                                  value={selectedAddUserId || ''}
                                  onChange={e =>
                                    setSelectedAddUserId(
                                      e.target.value ? Number(e.target.value) : null
                                    )
                                  }
                                >
                                  <option value=''>-- 選擇要加入的使用者 --</option>
                                  {available.map(u => (
                                    <option key={u.id} value={u.id}>
                                      {u.name}
                                      {u.position
                                        ? ` (${positionMap[u.position] || u.position})`
                                        : ''}{' '}
                                      — {u.email}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  className='member-action-btn add-btn'
                                  disabled={!selectedAddUserId}
                                  onClick={() => {
                                    const u = available.find(x => x.id === selectedAddUserId);
                                    if (!u) return;
                                    setPendingAdd(prev => [...prev, u]);
                                    setSelectedAddUserId(null);
                                  }}
                                >
                                  加入
                                </button>
                              </div>
                            ) : (
                              <p className='no-members-hint'>目前沒有未分配部門的使用者</p>
                            )}
                            {pendingAdd.length > 0 && (
                              <div className='pending-list'>
                                <div className='pending-list-label'>待加入：</div>
                                {pendingAdd.map(u => (
                                  <div key={u.id} className='pending-tag pending-tag-add'>
                                    {u.name}
                                    <button
                                      className='pending-cancel'
                                      onClick={() =>
                                        setPendingAdd(prev => prev.filter(x => x.id !== u.id))
                                      }
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div className='modal-footer'>
                    <button
                      className='confirm-button'
                      disabled={pendingAdd.length === 0 && pendingRemove.length === 0}
                      onClick={handleConfirmMemberChanges}
                    >
                      確定
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default DepartmentManagementPage;
